import { Model } from 'app/model/backendmodel';

import { FileTypeDataset } from '../../dataset/file-type.dataset';
import { DatasetAdapter } from './dataset.adapter';
import { FileFormat } from '../../dataset/file-format';
import { fileFormats, columnDelimiterOptions, rowDelimiterOptions } from 'app/shared/components/fileformat/fileformat.constants';
import { TextFormat } from 'app/model/dataset/text-format';
import { JSONFormat } from 'app/model/dataset/json.format';
import { Compression } from 'app/model/dataset/compression';
import { compressionOptionValues } from 'app/shared/components/compression-setting/compression-options';
import { FileShareDataset } from 'app/model/dataset/file-share.dataset';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { TypeFormat } from 'app/model/dataset/type-format';
import { string2UnicodeSingle, unicode2StrSingle } from 'app/shared/utils/string.util';
import { IAttribute } from 'app/model/base/annotation';
import { Owned } from 'app/model/base/named';

export interface IFormatAndCompression {
    format: Model.DatasetStorageFormat | undefined;
    compression: Model.DatasetCompression | undefined;
}

export type DatasetLevelCompression = Model.DatasetGZipCompression | Model.DatasetDeflateCompression;

export const formatAdapts = {
    type: '*',
    // text properties
    columnDelimiter: '*',
    rowDelimiter: '*',
    escapeChar: '*',
    quoteChar: '*',
    nullValue: '*',
    encodingName: '*',
    treatEmptyAsNull: '*',
    skipLineCount: '*',
    firstRowAsHeader: '*',
    // json properties
    nestingSeparator: '*',
    filePattern: '*',
    jsonNodeReference: '*',
    jsonPathDefinition: '*'
};

export const compressionAdapts = {
    type: '*',
    level: '*'
};

export function convertFormatResourceToValue(format: Model.DatasetStorageFormat | undefined, owner?: Owned): FileFormat | undefined {
    const fileFormat = owner ? Owned.create<FileFormat>(FileFormat.MODEL, owner) : new FileFormat();
    if (format == null) {
        // here we set format type as noFormat in dataset model referring to null format in dataset resource
        fileFormat.type = fileFormats.noFormat;
        return fileFormat;
    }
    fileFormat.type = format.type;
    switch (format.type) {
        case fileFormats.delimitedTextFormat:
        case fileFormats.textFormat:
            fileFormat.type = fileFormats.textFormat;
            const textFormat = format as Model.TextFormat;
            const textFormatValue = new TextFormat();

            ValueAdapter.toModel(textFormatValue, TypeFormat.ENCODING_NAME, textFormat.encodingName);
            textFormatValue.columnDelimiter = convertUnicode2Str(textFormatValue, TextFormat.COLUMN_DELIMETER, textFormat.columnDelimiter, columnDelimiterOptions.map((option) => option.value));
            if (textFormatValue.columnDelimiter && !textFormatValue.columnDelimiter.startsWith('\\u')) {
                // reserve \\ to avoid second escape
                textFormatValue.columnDelimiter = textFormatValue.columnDelimiter.replace(/\\/g, '\\\\');
            }
            textFormatValue.rowDelimiter = convertUnicode2Str(textFormatValue, TextFormat.ROW_DELIMETER, textFormat.rowDelimiter, rowDelimiterOptions.map((option) => option.value));
            if (textFormatValue.rowDelimiter && !textFormatValue.rowDelimiter.startsWith('\\u')) {
                // reserve \\ to avoid second escape
                textFormatValue.rowDelimiter = textFormatValue.rowDelimiter.replace(/\\/g, '\\\\');
            }
            ValueAdapter.toModel(textFormatValue, TextFormat.SKIP_LINE_COUNT, textFormat.skipLineCount);
            ValueAdapter.toModel(textFormatValue, TextFormat.FIRST_ROW_AS_HEADER, textFormat.firstRowAsHeader);
            if (textFormat.treatEmptyAsNull == null) {// default value for treatemptyasnull is true
                textFormatValue.treatEmptyAsNull = true;
            } else {
                ValueAdapter.toModel(textFormatValue, TextFormat.TREAT_EMPTY_AS_NULL, textFormat.treatEmptyAsNull);
            }
            textFormatValue.escapeChar = convertUnicode2Str(textFormatValue, TextFormat.ESCAPE_CHAR, textFormat.escapeChar);
            textFormatValue.quoteChar = convertUnicode2Str(textFormatValue, TextFormat.QUOTE_CHAR, textFormat.quoteChar);
            ValueAdapter.toModel(textFormatValue, TextFormat.NULL_VALUE, textFormat.nullValue);

            Owned.own(fileFormat, 'typeFormat', textFormatValue);
            break;
        case fileFormats.jsonFormat:
            const jsonFormat = format as Model.JsonFormat;
            const jsonFormatValue = new JSONFormat();

            ValueAdapter.toModel(jsonFormatValue, TypeFormat.ENCODING_NAME, jsonFormat.encodingName);
            ValueAdapter.toModel(jsonFormatValue, JSONFormat.NESTING_SEPARATOR, jsonFormat.nestingSeparator);

            jsonFormatValue.filePattern = jsonFormat.filePattern;
            jsonFormatValue.jsonNodeReference = jsonFormat.jsonNodeReference;
            if (jsonFormat.jsonPathDefinition) {
                Object.keys(jsonFormat.jsonPathDefinition).forEach((key) => {
                    jsonFormatValue.jsonPathDefinition.push({
                        columnName: key,
                        jsonPath: jsonFormat.jsonPathDefinition[key]
                    });
                });
            }

            Owned.own(fileFormat, 'typeFormat', jsonFormatValue);
            break;
        default:
            fileFormat.typeFormat = undefined;
    }
    return fileFormat;
}

export function convertCompressionResourceToValue(compression: Model.DatasetCompression | undefined): Compression | undefined {
    if (compression === undefined) {
        return undefined;
    }
    const result = new Compression();
    result.compressionType = compression.type;
    result.compressionLevel = (compression as DatasetLevelCompression).level;
    return result;
}

export function convertFormatValueToResource(formatValue: FileFormat | undefined): Model.DatasetStorageFormat | undefined {
    let format: Model.DatasetStorageFormat | undefined;
    if (!formatValue || !formatValue.type || (formatValue.type === fileFormats.noFormat)) {
        format = undefined;
    } else {
        switch (formatValue.type) {
            case fileFormats.textFormat:
                (() => {
                    const typeFormat: TextFormat = formatValue.typeFormat || Owned.create(TextFormat.MODEL, formatValue);
                    const encodingName = ValueAdapter.toResource(typeFormat, TextFormat.ENCODING_NAME);
                    const textFormat: Model.TextFormat = {
                        type: formatValue.type,
                        columnDelimiter: convertStr2SingleUnicode(typeFormat, TextFormat.COLUMN_DELIMETER),
                        rowDelimiter: convertStr2SingleUnicode(typeFormat, TextFormat.ROW_DELIMETER),
                        escapeChar: convertStr2SingleUnicode(typeFormat, TextFormat.ESCAPE_CHAR),
                        quoteChar: convertStr2SingleUnicode(typeFormat, TextFormat.QUOTE_CHAR),
                        nullValue: ValueAdapter.toResource(typeFormat, TextFormat.NULL_VALUE),
                        encodingName: encodingName === '' ? undefined : encodingName,
                        treatEmptyAsNull: ValueAdapter.toResource(typeFormat, TextFormat.TREAT_EMPTY_AS_NULL),
                        skipLineCount: ValueAdapter.toResource(typeFormat, TextFormat.SKIP_LINE_COUNT) != null ? ValueAdapter.toResource(typeFormat, TextFormat.SKIP_LINE_COUNT) : undefined,
                        firstRowAsHeader: ValueAdapter.toResource(typeFormat, TextFormat.FIRST_ROW_AS_HEADER)
                    };

                    // remove extra slash when user manually enters a column or row delimiter. (typing \t will result in \\t otherwise)
                    const columnDelimiter = ValueAdapter.toResource(typeFormat, TextFormat.COLUMN_DELIMETER);
                    try {
                        textFormat.columnDelimiter = JSON.parse(JSON.stringify(columnDelimiter).replace(/\\\\/g, '\\'));
                    } catch {
                        textFormat.columnDelimiter = columnDelimiter;
                    }
                    if (textFormat.columnDelimiter != null && typeof textFormat.columnDelimiter === 'string') {
                        textFormat.columnDelimiter = string2UnicodeSingle(textFormat.columnDelimiter);
                    }
                    const rowDelimiter = ValueAdapter.toResource(typeFormat, TextFormat.ROW_DELIMETER);
                    try {
                        textFormat.rowDelimiter = JSON.parse(JSON.stringify(rowDelimiter).replace(/\\\\/g, '\\'));
                    } catch {
                        textFormat.rowDelimiter = rowDelimiter;
                    }
                    if (textFormat.rowDelimiter != null && typeof textFormat.rowDelimiter === 'string') {
                        textFormat.rowDelimiter = string2UnicodeSingle(textFormat.rowDelimiter);
                    }

                    format = textFormat;
                })();
                break;
            case fileFormats.jsonFormat:
                (() => {
                    const typeFormat: JSONFormat = formatValue.typeFormat as JSONFormat || Owned.create(JSONFormat.MODEL, formatValue);

                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    let jsonPathDefinition: any = {};
                    if (typeFormat.jsonPathDefinition && typeFormat.jsonPathDefinition.length > 0) {
                        typeFormat.jsonPathDefinition.forEach((item) => {
                            jsonPathDefinition[item.columnName] = item.jsonPath;
                        });
                    } else {
                        jsonPathDefinition = undefined;
                    }

                    const encodingName = ValueAdapter.toResource(typeFormat, TypeFormat.ENCODING_NAME);

                    const jsonFormat: Model.JsonFormat = {
                        type: formatValue.type,
                        nestingSeparator: ValueAdapter.toResource(typeFormat, JSONFormat.NESTING_SEPARATOR),
                        encodingName: encodingName === '' ? undefined : encodingName,
                        filePattern: typeFormat.filePattern ? typeFormat.filePattern : undefined,
                        jsonNodeReference: typeFormat.jsonNodeReference ? typeFormat.jsonNodeReference : undefined,
                        jsonPathDefinition
                    };
                    format = jsonFormat;
                })();
                break;
            case fileFormats.avroFormat:
            case fileFormats.orcFormat:
            case fileFormats.parquetFormat:
                format = {
                    type: formatValue.type
                };
                break;
            default:
                format = undefined;
        }
    }
    return format;
}

export function convertCompressionValueToResource(compresionValue: Compression | undefined): Model.DatasetCompression | undefined {
    let compression: DatasetLevelCompression | undefined;
    if (compresionValue === undefined || compresionValue.compressionType === compressionOptionValues.None || compresionValue.compressionType === undefined) {
        compression = undefined;
    } else {
        compression = {
            type: compresionValue.compressionType,
            level: compresionValue.compressionLevel
        };
    }

    return compression;
}

export abstract class FileTypeDatasetAdapter<T extends FileTypeDataset, U extends Model.DatasetResource> extends DatasetAdapter<T, U> {
    protected formatAndCompressionValueToResource(formatValue: FileFormat | undefined, compressionValue: Compression | undefined): IFormatAndCompression {
        return {
            format: convertFormatValueToResource(formatValue),
            compression: convertCompressionValueToResource(compressionValue)
        };
    }

    protected formatResourceToValue(format: Model.DatasetStorageFormat | undefined): FileFormat | undefined {
        return convertFormatResourceToValue(format);
    }

    protected compressionResourceToValue(compression: Model.DatasetCompression | undefined): Compression | undefined {
        return convertCompressionResourceToValue(compression);
    }

    protected initializeModel(dataset: FileShareDataset): void {
        if (dataset != null) {
            dataset.format = undefined;
            dataset.compression = undefined;
        }
    }

}

function convertStr2SingleUnicode(typeFormat: TypeFormat, attribute: IAttribute): string | undefined {
    let str = ValueAdapter.toResource(typeFormat, attribute);
    if (str != null && str.type !== 'Expression') {
        str = string2UnicodeSingle(str);
    }
    return str;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function convertUnicode2Str(textFormat: TextFormat, attribute: IAttribute, modelValue: any, skipSymbols: string[] = []): string | undefined {
    if (modelValue != null) {
        if (modelValue.type === 'Expression') {
            ValueAdapter.toModel(textFormat, attribute, modelValue);
        } else {
            return unicode2StrSingle(modelValue, skipSymbols);
        }
    }
    return undefined;
}
